import { useEffect, useRef, useState } from "react";
import { MdSearch } from "react-icons/md";
import { PiX } from "react-icons/pi";

import { Box, Button, Flex, Input, InputGroup, RadioGroup, Stack } from "@chakra-ui/react";
import { rem } from "polished";

import { RadioOption } from "./radio-option";
import { RadioOptionType as SelectOptionType } from "./types";
import { normalizeString } from "./utils";

type RadioSelectListProps<T extends string> = {
  name: string;
  value?: T | null;
  onChangeValue?: (value: T) => void;
  options: SelectOptionType<T>[];
  focusOnMount?: boolean;
  allowUnselect?: boolean;
};

export const RadioSelectList = <T extends string>({
  name,
  options,
  value,
  onChangeValue,
  focusOnMount = false,
  allowUnselect = false,
}: RadioSelectListProps<T>) => {
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && focusOnMount) inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef]);

  const selectedOption = options.find(
    option => value === option.value && normalizeString(value).includes(normalizeString(search))
  );

  const filteredOptions = options.filter(option =>
    normalizeString(option.label).includes(normalizeString(search))
  );

  return (
    <Box paddingBlock="6px" paddingInline={0}>
      <InputGroup marginInline="6px" paddingLeft={1} startElement={<MdSearch size={rem(20)} />}>
        <Input
          borderColor="transparent"
          _focus={{ outline: "none" }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          ref={inputRef}
          size="md"
          placeholder="Rechercher..."
        />
      </InputGroup>
      <RadioGroup.Root value={value} onValueChange={e => onChangeValue?.(e.value as T)}>
        {/* selected options part */}
        {selectedOption && (
          <>
            <Stack paddingInline="6px" py={1} direction="column" gap={1}>
              <Flex alignItems="center" justifyContent="space-between">
                <RadioOption
                  label={selectedOption.label}
                  value={selectedOption.value}
                  name={name}
                  onChangeValue={() => onChangeValue?.(selectedOption.value)}
                />
                {allowUnselect && (
                  <Button size="sm" variant="ghost" onClick={() => onChangeValue?.("" as T)}>
                    <PiX />
                  </Button>
                )}
              </Flex>
            </Stack>
            <Box h="2px" backgroundColor="gray.200" />
          </>
        )}
        {/*  all options part */}
        <Stack
          paddingInline="6px"
          overflow="auto"
          py={1}
          direction="column"
          gap={1}
          maxHeight="300px"
        >
          {filteredOptions.map(option => (
            <RadioOption
              key={option.value}
              label={option.label}
              value={option.value}
              name={name}
              onChangeValue={() => onChangeValue?.(option.value)}
              disabled={option.disabled || value === option.value}
            />
          ))}
          {filteredOptions.length === 0 && (
            <Box paddingInline="6px" py={1} textAlign="center" color="gray.500">
              Aucun résultat de recherche...
            </Box>
          )}
        </Stack>
      </RadioGroup.Root>
    </Box>
  );
};
